// The in-memory Agents twin (M3-08): runs and their steps, approvals, budgets, a DE's budget and
// agent composition, and the wake-up / admission events, with the API's rules as the M3-01
// contracts and spec 05 state them. On the real API, runs (`runs.list`, `runs.get`,
// `runs.cancel`), the agent composition and the events feed have handlers (M3-02, M3-06);
// approvals (M3-03) and budgets (M3-04) do not yet, and the screens run on this twin until they
// land. `fake-agents-contract.test.ts` holds every payload to its route's zod schema.
//
// Rules kept:
// - `agents` is an add-on (403 `feature_disabled` when off); budgets and the DE budget are `core`.
// - Runs: a company manager sees every run; anyone else none (they hold no DE seat in the demo).
//   Cancel only a live run (409 `run_state_conflict`), releasing its reservation.
// - Approvals (05 `approval`): decider = approver#holder, people only; viewer = decider | requester
//   | company#admin. The demo accounts hold no seat, so the company's owner stands in for the
//   holder of every approver seat (the one simplification). A second decision returns the first
//   (idempotent); an expired approval is 409 `approval_expired`; reject needs a reason. Approving
//   a `budget_overrun` raises the DE's holder budget by `change.extra_minor`.
// - Budgets: managers only (403 otherwise); one active budget per (subject, period).
// - Events: managers only, ascending `seq`, `after_seq` cursor, `types` filter.
import { RuntimeQuery } from "@companyos/schema/contracts";
import type { OperationOutput, RouteId } from "@companyos/sdk";
import { type Handler, json, notFound, type Principal, page, problem, validation } from "./fake";
import { executionAfterDecision, NO_EXECUTION } from "./fake-approval-execution";
import type { FakeCompany, FakeDe } from "./fake-data";
import { uid } from "./fake-data";
import { kpiEditProblem } from "./fake-reporting";
import { FIRST_ATTEMPT, personSeesRun, seatsBelow } from "./fake-runs";

type Run = OperationOutput<"runs.list">["data"][number];
type RunStep = OperationOutput<"runs.get">["steps"][number];
type Approval = OperationOutput<"my.approvals">["data"][number];
type Budget = OperationOutput<"budgets.list">["data"][number];
type BudgetIncident = OperationOutput<"budgets.get">["incidents"][number];
type AgentProfile = OperationOutput<"digitalEmployees.agents">["data"][number];
type Event = OperationOutput<"events.list">["data"][number];
type Delegation = OperationOutput<"delegationRequests.get">;

export type AgentsDeps = {
  now: () => Date;
  companies: FakeCompany[];
  desOf: (companyId: string) => FakeDe[];
  /** The membership of the company's owner account, or null. */
  ownerMembership: (companyId: string) => string | null;
  featureOn: (p: Principal, feature: "agents") => boolean;
  /** Runs another twin holds (the chat twin's demo runs), listed after this twin's own. */
  extraRuns?: (companyId: string) => readonly Run[];
  /**
   * DC-11: hands other twins a read of the seat budgets (the self-serve deploy refuses a seat
   * without an active day, week or month budget, as the bundle builder does).
   */
  shareSeatBudget?: (has: (companyId: string, seatId: string) => boolean) => void;
  /** M5-04: the finance twin's budget vs actual reads the company's budgets. */
  shareBudgets?: (of: (companyId: string) => Budget[]) => void;
  /** M5-05: hands the agency console a read of a company's approvals (every state). */
  shareApprovals?: (read: (companyId: string) => readonly Approval[]) => void;
  /** WF-FIX: hands the DE profile a read of every run the twins know (`activity.last_run_at`). */
  shareRuns?: (read: (companyId: string) => readonly Run[]) => void;
  /** AGENT-REPORTING: more seeded runs, steps and approvals (the reporting runs' review). */
  seedMore?: (
    company: FakeCompany,
    s: { runs: Run[]; steps: Map<string, RunStep[]>; approvals: Approval[] },
  ) => void;
  /** AGENT-REPORTING: an approval was approved or edited (a `kpi_value` records its value). */
  onDecided?: (company: FakeCompany, a: Approval) => void;
  /** ACTIVITY: the Work twin's live tasks (a run's `task_id` and the feed's task title). */
  tasksOf?: (companyId: string) => readonly { id: string; title: string; board_id: string }[];
  /** ACTIVITY: Who = an AG (the membership it reports to) or a team (its seats). */
  whoOf?: (
    p: Principal,
    kind: "ag" | "team",
    id: string,
  ) => { memberships?: string[]; seats?: string[] };
};

type AgentsState = {
  runs: Run[];
  steps: Map<string, RunStep[]>;
  approvals: Approval[];
  budgets: Budget[];
  incidents: BudgetIncident[];
  profiles: AgentProfile[];
  events: Event[];
  delegations: Delegation[];
};

const LIVE: readonly Run["state"][] = [
  "queued",
  "running",
  "waiting_input",
  "awaiting_approval",
  "waiting_children",
];
const MIN = 60_000;
const HOUR = 60 * MIN;
const companyNo = (id: string) => Number.parseInt(id.slice(-4), 16) % 0x0fff;
const forbidden = (detail = "managers only") => problem(403, "forbidden", "Forbidden", detail);

function featureDisabled(): Response {
  return new Response(
    JSON.stringify({
      type: "https://companyos.app/errors/feature_disabled",
      title: "Feature not enabled",
      status: 403,
      code: "feature_disabled",
      feature: "agents",
      request_id: `fake-${Math.random().toString(36).slice(2, 10)}`,
      detail: "The agents add-on is not enabled for this company.",
    }),
    { status: 403, headers: { "content-type": "application/problem+json" } },
  );
}

/** The step script of a seeded run: kind, what it did, and its metering. */
type StepSpec = {
  kind: RunStep["kind"];
  payload: Record<string, unknown>;
  tokens?: [number, number];
  cost?: number;
  ms?: number;
};

const SCRIPTS: Record<string, StepSpec[]> = {
  sourcing: [
    {
      kind: "model_call",
      payload: { summary: "Read the open Senior AE requisition and the hiring manager's notes" },
      tokens: [6200, 640],
      cost: 31_000,
      ms: 4200,
    },
    {
      kind: "tool_call",
      payload: { tool: "ats.search_candidates", summary: "Searched the ATS: 42 matches" },
      cost: 2_000,
      ms: 1800,
    },
    {
      kind: "model_call",
      payload: { summary: "Ranked the matches against the scorecard; kept 8" },
      tokens: [14_800, 1_900],
      cost: 78_000,
      ms: 9100,
    },
    {
      kind: "note",
      payload: { summary: "Shortlist posted to the Senior AE task" },
      ms: 300,
    },
  ],
  scheduling: [
    {
      kind: "model_call",
      payload: { summary: "Read three candidates' availability from the inbox thread" },
      tokens: [4_100, 420],
      cost: 19_000,
      ms: 3100,
    },
    {
      kind: "tool_call",
      payload: { tool: "calendar.free_busy", summary: "Checked the panel's calendars" },
      cost: 1_000,
      ms: 900,
    },
    {
      kind: "proposal",
      payload: { summary: "Proposed sending three interview invites (needs approval)" },
      tokens: [2_300, 610],
      cost: 14_000,
      ms: 2400,
    },
  ],
  heartbeat: [
    {
      kind: "model_call",
      payload: { summary: "Morning check: 2 new applications, 1 overdue follow-up" },
      tokens: [3_000, 300],
      cost: 13_000,
      ms: 2600,
    },
    {
      kind: "tool_call",
      payload: { tool: "tasks.update", summary: "Moved the overdue follow-up to today" },
      cost: 1_000,
      ms: 700,
    },
  ],
  failing: [
    {
      kind: "model_call",
      payload: { summary: "Drafted a reply to the candidate's question about salary bands" },
      tokens: [2_600, 380],
      cost: 12_000,
      ms: 2100,
    },
    {
      kind: "tool_call",
      payload: { tool: "ats.update_stage", summary: "The ATS timed out after 30 s", error: true },
      cost: 0,
      ms: 30_000,
    },
  ],
  running: [
    {
      kind: "model_call",
      payload: { summary: "Reading the new Finance Manager requisition" },
      tokens: [5_100, 520],
      cost: 24_000,
      ms: 3800,
    },
    {
      kind: "tool_call",
      payload: { tool: "ats.search_candidates", summary: "Searching the ATS" },
      cost: 2_000,
      ms: 1500,
    },
  ],
  cancelled: [
    {
      kind: "model_call",
      payload: { summary: "Started a weekly pipeline digest; cancelled by the supervisor" },
      tokens: [1_900, 120],
      cost: 8_000,
      ms: 1600,
    },
  ],
};

/** One seeded run per entry, oldest first; `ago` is when it was queued. */
const RUN_PLAN: {
  script: keyof typeof SCRIPTS | null;
  state: Run["state"];
  trigger: Run["trigger_kind"];
  ref: NonNullable<Run["trigger_ref"]>["kind"] | null;
  ago: number;
  span: number;
}[] = [
  {
    script: "sourcing",
    state: "completed",
    trigger: "heartbeat",
    ref: "schedule",
    ago: 27 * HOUR,
    span: 18_000,
  },
  {
    script: "sourcing",
    state: "completed",
    trigger: "inbox",
    ref: "task",
    ago: 21 * HOUR,
    span: 22_000,
  },
  {
    script: "failing",
    state: "failed",
    trigger: "mention",
    ref: "message",
    ago: 9 * HOUR,
    span: 33_000,
  },
  {
    script: "cancelled",
    state: "cancelled",
    trigger: "manual",
    ref: "manual",
    ago: 6 * HOUR,
    span: 1_600,
  },
  {
    script: "scheduling",
    state: "awaiting_approval",
    trigger: "inbox",
    ref: "task",
    ago: 3 * HOUR,
    span: 6_400,
  },
  {
    script: "heartbeat",
    state: "completed",
    trigger: "heartbeat",
    ref: "schedule",
    ago: 90 * MIN,
    span: 3_300,
  },
  { script: "running", state: "running", trigger: "inbox", ref: "task", ago: 4 * MIN, span: 0 },
  { script: null, state: "queued", trigger: "mention", ref: "message", ago: 1 * MIN, span: 0 },
];

const HAND_BACKS: Record<string, NonNullable<Run["hand_back"]>> = {
  sourcing: {
    summary: "Eight candidates shortlisted for Senior AE, ranked against the scorecard.",
    next_steps: ["Review the shortlist", "Pick three for a first call"],
    needs: "review",
  },
  heartbeat: {
    summary: "Morning check done: two new applications triaged, one follow-up moved to today.",
    needs: "nothing",
  },
};

const iso = (ms: number) => new Date(ms).toISOString();
const sum = (xs: number[]) => xs.reduce((a, b) => a + b, 0);

export function createAgentsFake(deps: AgentsDeps): Partial<Record<RouteId, Handler>> {
  const states = new Map<string, AgentsState>();
  let seq = 0x100;
  let eventSeq = 0;
  const stamp = () => deps.now().toISOString();

  function monthKey(at: Date): string {
    return at.toISOString().slice(0, 7);
  }

  function stateOf(company: FakeCompany): AgentsState {
    const existing = states.get(company.id);
    if (existing) return existing;
    const s: AgentsState = {
      runs: [],
      steps: new Map(),
      approvals: [],
      budgets: [],
      incidents: [],
      profiles: [],
      events: [],
      delegations: [],
    };
    states.set(company.id, s);
    seed(company, s);
    return s;
  }

  function event(
    s: AgentsState,
    company: FakeCompany,
    type: Event["type"],
    subject: Event["subject"],
    payload: Record<string, unknown>,
    at: number,
    actor: Event["actor"] = { kind: "system" },
  ) {
    eventSeq += 1;
    s.events.push({
      id: uid(0x9900, companyNo(company.id) * 0x10000 + eventSeq),
      company_id: company.id,
      seq: eventSeq,
      type,
      version: 1,
      actor,
      subject,
      payload,
      correlation_id: null,
      causation_id: null,
      occurred_at: iso(at),
    });
  }

  function seed(company: FakeCompany, s: AgentsState) {
    const now = deps.now().getTime();
    // A not-deployed DE never ran: no history to seed.
    const des = deps
      .desOf(company.id)
      .filter((d) => !d.fresh && d.status !== "retired" && d.status !== "not_deployed");
    const no = companyNo(company.id);
    const month = monthKey(deps.now());
    type Draft = { de: FakeDe; plan: (typeof RUN_PLAN)[number]; index: number };
    const drafts: Draft[] = [];
    des.forEach((de, i) => {
      RUN_PLAN.forEach((plan, k) => {
        // A paused DE has nothing live; only the first DE has a queued run waiting.
        if (de.status === "paused" && LIVE.includes(plan.state)) return;
        if (plan.state === "queued" && i > 0) return;
        drafts.push({ de, plan: { ...plan, ago: plan.ago + i * 7 * MIN }, index: k });
      });
    });
    drafts.sort((a, b) => b.plan.ago - a.plan.ago);
    // ACTIVITY: a run woken by a task works on one of the company's real (Work twin) tasks.
    const tasks = deps.tasksOf?.(company.id) ?? [];
    drafts.forEach(({ de, plan }, n) => {
      const id = uid(0x9100, no * 0x1000 + n + 1);
      const created = now - plan.ago;
      const specs = plan.script ? (SCRIPTS[plan.script] ?? []) : [];
      const steps: RunStep[] = specs.map((sp, k) => ({
        company_id: company.id,
        id: uid(0x9200, no * 0x10000 + (n + 1) * 16 + k + 1),
        run_id: id,
        seq: k + 1,
        kind: sp.kind,
        payload: sp.payload,
        cost_micros: sp.cost ?? 0,
        tokens_in: sp.tokens?.[0] ?? 0,
        tokens_out: sp.tokens?.[1] ?? 0,
        model: sp.kind === "model_call" || sp.kind === "proposal" ? "claude-sonnet" : null,
        duration_ms: sp.ms ?? null,
        created_at: iso(created + 2_000 + k * 1_500),
      }));
      const ended = ["completed", "failed", "cancelled"].includes(plan.state);
      const started = plan.state === "queued" ? null : created + 1_500;
      const ref =
        plan.ref === null
          ? null
          : {
              kind: plan.ref,
              id:
                plan.ref === "manual" || plan.ref === "schedule"
                  ? null
                  : uid(0x9300, no * 0x1000 + n + 1),
            };
      const live = LIVE.includes(plan.state);
      const run: Run = {
        company_id: company.id,
        id,
        membership_id: de.membership_id,
        crew_agent_id: null,
        seat_id: de.seat_id,
        agent_profile_id: uid(0x9400, no * 16 + des.indexOf(de) + 1),
        owner_membership_id: null,
        identity_mode: "as_agent",
        trigger_kind: plan.trigger,
        trigger_ref: ref,
        wakeup_ids: [uid(0x9500, no * 0x1000 + n + 1)],
        state: plan.state,
        state_reason:
          plan.state === "failed"
            ? "tool_error: ats.update_stage timed out"
            : plan.state === "cancelled"
              ? `cancelled by ${de.supervisor.name}`
              : plan.state === "awaiting_approval"
                ? "approval"
                : null,
        started_at: started === null ? null : iso(started),
        ended_at: ended ? iso(created + plan.span) : null,
        parent_run_id: null,
        delegation_depth: 0,
        budget_reserved_micros: live ? 250_000 : 0,
        cost_micros: sum(steps.map((x) => x.cost_micros)),
        tokens_in: sum(steps.map((x) => x.tokens_in)),
        tokens_out: sum(steps.map((x) => x.tokens_out)),
        model: plan.script ? "claude-sonnet" : null,
        sandbox_kind: null,
        skill_pack_versions: { recruiting: "1.4.0" },
        summary:
          plan.script && plan.state === "completed"
            ? (HAND_BACKS[plan.script]?.summary ?? null)
            : null,
        hand_back:
          plan.script && plan.state === "completed" ? (HAND_BACKS[plan.script] ?? null) : null,
        lease_owner: plan.state === "running" ? "runtime" : null,
        lease_until: plan.state === "running" ? iso(now + MIN) : null,
        verdict: plan.state === "completed" ? "verified" : null,
        verdict_evidence_ref: null,
        output_ref: null,
        model_path: "gateway",
        model_path_fallback_used: false,
        model_credential_id: null,
        context_refs: null,
        bundle_sha: null,
        ...FIRST_ATTEMPT,
        task_id:
          ref?.kind === "task" && tasks.length ? (tasks[n % tasks.length]?.id ?? null) : null,
        actor_kind: "system",
        actor_membership_id: null,
        created_at: iso(created),
        updated_at: iso(ended ? created + plan.span : (started ?? created)),
      };
      s.runs.push(run);
      s.steps.set(id, steps);
      event(
        s,
        company,
        "wakeup.requested",
        { kind: "wakeup_request", id: run.wakeup_ids[0] as string },
        { agent_membership_id: de.membership_id, kind: plan.trigger, source_ref: ref },
        created - 1_000,
      );
      // ACTIVITY: a run on a task moved it along (the Activity trail and "last" line).
      const task = tasks.find((t) => t.id === run.task_id);
      if (task && started !== null)
        event(
          s,
          company,
          "task.status_changed",
          { kind: "task", id: task.id },
          { board_id: task.board_id, from: "todo", to: "in_progress", completed: false },
          started + 3_000,
          {
            kind: "digital_employee",
            membership_id: de.membership_id,
            seat_id: de.seat_id,
            run_id: id,
          },
        );
    });

    // DC-08: the first DE's first finished run spawned an ephemeral worker (depth 1, scoped to
    // one task and its thread, with the DE's standing orders) and a second one admission
    // refused: the DE's bundle had no standing orders block at that point.
    const spawner = des[0];
    const parent = s.runs.find(
      (r) => r.membership_id === spawner?.membership_id && r.state === "completed",
    );
    if (spawner && parent) {
      const at = Date.parse(parent.created_at) + 60_000;
      const workerId = uid(0x9200, no * 0x1000 + 0xe01);
      const task = uid(0x9300, no * 0x1000 + 0xe01);
      s.runs.push({
        ...parent,
        id: workerId,
        agent_profile_id: uid(0x9400, no * 16 + 0xe),
        trigger_kind: "delegation",
        trigger_ref: { kind: "run", id: parent.id },
        wakeup_ids: [],
        parent_run_id: parent.id,
        delegation_depth: 1,
        budget_reserved_micros: 0,
        cost_micros: 18_000,
        tokens_in: 2_100,
        tokens_out: 640,
        summary: "Screened the shortlist on the task; two candidates flagged for review.",
        hand_back: null,
        verdict: null,
        skill_pack_versions: {},
        context_refs: [
          { kind: "task", id: task },
          { kind: "thread", id: uid(0x9300, no * 0x1000 + 0xe02) },
        ],
        bundle_sha: "5f0c".padEnd(64, "0"),
        created_at: iso(at),
        started_at: iso(at + 2_000),
        ended_at: iso(at + 92_000),
        updated_at: iso(at + 92_000),
      });
      s.steps.set(workerId, []);
      event(
        s,
        company,
        "run.worker_refused",
        { kind: "run", id: parent.id },
        {
          agent_membership_id: spawner.membership_id,
          worker_profile_id: uid(0x9400, no * 16 + 0xe),
          reason: "standing_orders_missing",
          detail: "bundle has no standing orders",
        },
        at - 30_000,
      );
    }

    // Admission refusals: the second DE's budget had no room for one wake-up; a paused DE's
    // mention was dropped.
    const second = des[1];
    if (second) {
      const at = now - 2 * HOUR;
      const wid = uid(0x9500, no * 0x1000 + 0xf01);
      event(
        s,
        company,
        "wakeup.requested",
        { kind: "wakeup_request", id: wid },
        {
          agent_membership_id: second.membership_id,
          kind: "mention",
          source_ref: { kind: "message", id: uid(0x9300, no * 0x1000 + 0xf01) },
        },
        at,
      );
      event(
        s,
        company,
        "budget.reservation_refused",
        { kind: "wakeup_request", id: wid },
        {
          level: "holder",
          budget_id: uid(0x9600, no * 16 + 2),
          estimate_micros: 250_000,
          shortfall_micros: 61_000,
        },
        at + 400,
      );
      event(
        s,
        company,
        "wakeup.dropped",
        { kind: "wakeup_request", id: wid },
        { agent_membership_id: second.membership_id, reason: "budget" },
        at + 500,
      );
    }
    for (const de of des.filter((d) => d.status === "paused")) {
      const wid = uid(0x9500, no * 0x1000 + 0xf02);
      event(
        s,
        company,
        "wakeup.requested",
        { kind: "wakeup_request", id: wid },
        { agent_membership_id: de.membership_id, kind: "mention", source_ref: null },
        now - 50 * MIN,
      );
      event(
        s,
        company,
        "wakeup.dropped",
        { kind: "wakeup_request", id: wid },
        { agent_membership_id: de.membership_id, reason: "paused" },
        now - 50 * MIN + 300,
      );
    }
    s.events.sort((a, b) => a.occurred_at.localeCompare(b.occurred_at));
    s.events.forEach((e, i) => {
      e.seq = i + 1;
    });
    eventSeq = Math.max(eventSeq, s.events.length);

    // Budgets: the company's month, each DE's holder month (the first DE near its cap).
    const budget = (
      n: number,
      subject_kind: Budget["subject_kind"],
      subject_id: string,
      amount_minor: number,
      hard_cap_minor: number | null,
    ): Budget => ({
      company_id: company.id,
      id: uid(0x9600, no * 16 + n),
      subject_kind,
      subject_id,
      period_type: "month",
      period_key: month,
      amount_minor,
      currency: company.reporting_currency,
      soft_thresholds: ["0.8"],
      hard_cap_minor,
      alert_only: false,
      repeat_monthly: true,
      inherit: false,
      state: "active",
      actor_kind: "person",
      actor_membership_id: deps.ownerMembership(company.id),
      created_at: iso(now - 20 * 24 * HOUR),
      updated_at: iso(now - 20 * 24 * HOUR),
    });
    if (des.length) s.budgets.push(budget(0, "company", company.id, 50_000, 60_000));
    des.forEach((de, i) => {
      s.budgets.push(
        budget(i + 1, "holder", de.membership_id, i === 0 ? 1_500 : 4_000, i === 0 ? 2_000 : null),
      );
    });
    const first = des[0];
    if (first) {
      s.incidents.push({
        company_id: company.id,
        id: uid(0x9700, no * 16 + 1),
        budget_id: uid(0x9600, no * 16 + 1),
        kind: "threshold",
        threshold: "0.8",
        period_key: month,
        run_id: null,
        amount_minor: 1_200,
        resolved_by_approval_id: null,
        resolved_at: null,
        created_at: iso(now - 5 * HOUR),
      });
    }

    // The agent composition: one lead per DE, plus a specialist for the first.
    des.forEach((de, i) => {
      const profile = (n: number, role: AgentProfile["role_in_owner"]): AgentProfile => ({
        company_id: company.id,
        id: uid(0x9400, no * 16 + n),
        template_id: uid(0x7d20, 1 + (role === "lead" ? 0 : 1)),
        template_version: role === "lead" ? 3 : 1,
        kind: "persistent",
        owner_kind: "digital_employee",
        owner_membership_id: de.membership_id,
        role_in_owner: role,
        overrides: {},
        memory_group_id: `${de.template_key}:${de.membership_id.slice(-4)}`,
        state: "active",
        actor_kind: "agency_user",
        actor_membership_id: null,
        created_at: iso(now - 30 * 24 * HOUR),
        updated_at: iso(now - 30 * 24 * HOUR),
      });
      s.profiles.push(profile(i + 1, "lead"));
      if (i === 0) s.profiles.push(profile(9, "specialist"));
    });

    // Delegation: the first DE's latest sourcing run hands the screening to the second DE.
    const [lead, helper] = des;
    const from = s.runs
      .filter((r) => r.membership_id === lead?.membership_id && r.state === "completed")
      .at(-1);
    if (lead && helper && from) {
      const id = uid(0x9a00, no * 16 + 1);
      s.delegations.push({
        company_id: company.id,
        id,
        from_membership_id: lead.membership_id,
        to_membership_id: helper.membership_id,
        to_seat_id: helper.seat_id,
        from_run_id: from.id,
        message_id: null,
        task_id: uid(0x9300, no * 0x1000 + 0xd01),
        context_refs: [{ kind: "task", id: uid(0x9300, no * 0x1000 + 0xd01) }],
        budget_micros: 400_000,
        deadline_at: iso(now + 26 * HOUR),
        state: "sent",
        depth: 1,
        root_request_id: id,
        child_run_id: null,
        result: null,
        decided_at: null,
        actor_kind: "digital_employee",
        actor_membership_id: lead.membership_id,
        created_at: iso(now - 40 * MIN),
        updated_at: iso(now - 40 * MIN),
      });
      // `runs/{id}/delegate` wakes the recipient (04).
      event(
        s,
        company,
        "wakeup.requested",
        { kind: "wakeup_request", id: uid(0x9500, no * 0x1000 + 0xf03) },
        {
          agent_membership_id: helper.membership_id,
          kind: "delegation",
          source_ref: { kind: "delegation_request", id },
        },
        now - 40 * MIN,
      );
      s.events.sort((a, b) => a.occurred_at.localeCompare(b.occurred_at));
      s.events.forEach((e, i) => {
        e.seq = i + 1;
      });
    }

    // Approvals: proposals of the runs that wait on one, a budget raise, and decided history.
    const approval = (
      n: number,
      de: FakeDe,
      kind: Approval["kind"],
      proposal: Approval["proposal"],
      opts: Partial<Approval> = {},
    ): Approval => ({
      company_id: company.id,
      id: uid(0x9800, no * 64 + n),
      kind,
      subject_kind: kind === "budget_overrun" ? "budget" : kind === "task_change" ? "task" : "run",
      subject_id: uid(0x9310, no * 64 + n),
      requested_by_seat_id: de.seat_id,
      requested_by_membership_id: de.membership_id,
      approver_seat_id: de.supervisor.seat_id,
      run_id: null,
      proposal,
      state: "pending",
      approved_payload: null,
      decided_at: null,
      decided_by_membership_id: null,
      reason: null,
      thread_id: null,
      expires_at: iso(now + 2 * 24 * HOUR),
      executed_at: null,
      ...NO_EXECUTION,
      actor_kind: "digital_employee",
      actor_membership_id: de.membership_id,
      created_at: iso(now - 3 * HOUR),
      updated_at: iso(now - 3 * HOUR),
      ...opts,
    });
    const waiting = s.runs.filter((r) => r.state === "awaiting_approval");
    des.forEach((de, i) => {
      const run = waiting.find((r) => r.membership_id === de.membership_id);
      if (!run) return;
      s.approvals.push(
        approval(
          i + 1,
          de,
          "external_action",
          {
            title: `Send interview invites to 3 candidates (${de.display_name})`,
            summary:
              "Three candidates agreed to Thursday slots; the invites go from the recruiting inbox.",
            change: {
              action: "email.send",
              to: ["ana.ruiz@example.com", "ben.okafor@example.com", "chen.li@example.com"],
              template: "panel_interview_invite",
              slot: "Thursday 10:00–11:00",
            },
            // PLAN-CHECK: the DE checked its plan against memory before asking
            plan_check: {
              verdict: i === 0 ? "conflict" : "no_conflict_found",
              check_id: uid(0x9811, no * 64 + i),
              plan: "Send panel interview invites for Thursday 10:00 to three candidates.",
              conflicts:
                i === 0
                  ? [
                      {
                        type: "Decision",
                        title: "No interview invites go out before the scorecard is approved",
                      },
                    ]
                  : [],
              checked_at: run.updated_at,
            },
          },
          {
            run_id: run.id,
            subject_kind: "run",
            subject_id: run.id,
            created_at: run.updated_at,
            updated_at: run.updated_at,
          },
        ),
      );
    });
    if (first) {
      s.approvals.push(
        approval(
          10,
          first,
          "task_change",
          {
            title: "Move “Senior AE” to Interviewing",
            summary: "The shortlist is ready and the hiring manager signed off on it.",
            change: { field: "status", from: "Sourcing", to: "Interviewing" },
          },
          { created_at: iso(now - 70 * MIN), updated_at: iso(now - 70 * MIN) },
        ),
        approval(
          11,
          first,
          "budget_overrun",
          {
            title: `Raise ${first.display_name}'s monthly budget`,
            summary: "80% of this month's budget is used with 11 days to go.",
            change: { extra_minor: 1_000, until: `${month}-28` },
          },
          {
            subject_id: uid(0x9600, no * 16 + 1),
            created_at: iso(now - 5 * HOUR),
            updated_at: iso(now - 5 * HOUR),
          },
        ),
        approval(
          12,
          first,
          "message",
          {
            title: "Reply to Ana Ruiz about the salary band",
            change: { body: "Thanks Ana: the band for this role is 95–110k, plus bonus." },
          },
          {
            state: "approved",
            approved_payload: {
              title: "Reply to Ana Ruiz about the salary band",
              change: { body: "Thanks Ana: the band for this role is 95–110k, plus bonus." },
            },
            decided_at: iso(now - 20 * HOUR),
            decided_by_membership_id: first.supervisor.membership_id,
            executed_at: iso(now - 20 * HOUR + 2_000),
            execution_state: "applied",
            execution_attempts: 1,
            created_at: iso(now - 22 * HOUR),
            updated_at: iso(now - 20 * HOUR),
          },
        ),
        approval(
          13,
          first,
          "external_action",
          {
            title: "Post the Finance Manager role on three job boards",
            change: { action: "jobs.post", boards: ["LinkedIn", "Indeed", "Glassdoor"] },
          },
          {
            state: "rejected",
            decided_at: iso(now - 26 * HOUR),
            decided_by_membership_id: first.supervisor.membership_id,
            reason: "Wait for the final job description from Finance.",
            created_at: iso(now - 30 * HOUR),
            updated_at: iso(now - 26 * HOUR),
          },
        ),
        // T1-BOARD-MCP: a template the DE proposed, planned by the server, applied by a manager
        approval(
          14,
          first,
          "template_apply",
          {
            title: "Apply template “Hiring pipeline”",
            summary: "Creates 1 space, 1 board. One board per open role keeps the stages apart.",
            change: {
              template_id: uid(0x9700, no * 16 + 1),
              params: { role: "Finance Manager" },
              plan: [
                { kind: "space", name: "Finance Manager hiring", action: "create" },
                {
                  kind: "board",
                  name: "Candidates",
                  action: "create",
                  parent: "Finance Manager hiring",
                },
                { kind: "role", name: "Recruiter", action: "reuse" },
              ],
            },
          },
          {
            state: "approved",
            subject_kind: "seat",
            decided_at: iso(now - 40 * HOUR),
            decided_by_membership_id: first.supervisor.membership_id,
            executed_at: iso(now - 40 * HOUR + 2_000),
            execution_state: "applied",
            execution_attempts: 1,
            created_at: iso(now - 41 * HOUR),
            updated_at: iso(now - 40 * HOUR),
          },
        ),
      );
    }
    deps.seedMore?.(company, s);
    s.approvals.sort((a, b) => b.created_at.localeCompare(a.created_at));
  }

  // Access --------------------------------------------------------------------------------------

  /** `agents` routes: 404 outside the company, 403 with the add-on off. */
  function agentsGate(p: Principal | null): Response | null {
    if (!p) return notFound();
    if (!deps.featureOn(p, "agents")) return featureDisabled();
    return null;
  }

  /** People who decide an approval: its approver seat's holder (the owner stands in, above). */
  function decides(p: Principal, a: Approval): boolean {
    const owner = deps.ownerMembership(p.company.id);
    const mine = p.me.memberships.find((m) => m.company_id === p.company.id)?.membership_id;
    if (mine && owner === mine) return true;
    const de = deps.desOf(p.company.id).find((d) => d.supervisor.seat_id === a.approver_seat_id);
    return Boolean(mine && de && de.supervisor.membership_id === mine);
  }

  /** viewer = decider | requester | company#admin (managers: owner, admin, agency operator). */
  function views(p: Principal, a: Approval): boolean {
    return p.manager || decides(p, a);
  }

  /** This twin's runs the caller may see (`visibleRuns` without the other twin's). */
  function runsFor(p: Principal): Run[] {
    const all = stateOf(p.company).runs;
    return all.filter(seesRun(p));
  }

  /** The caller's run rule (fake-runs `personSeesRun`, shared with the runs twin). */
  function seesRun(p: Principal): (r: Run) => boolean {
    if (p.manager) return () => true;
    const des = deps.desOf(p.company.id);
    const me = p.me.memberships.find((m) => m.company_id === p.company.id)?.membership_id ?? null;
    const seats = seatsBelow(des, me);
    return (r) => personSeesRun(des, me, r, seats);
  }

  /**
   * ACTIVITY: every run the caller may see, this twin's and the other twin's (`extraRuns`), by
   * one rule — `runs.list` and `workforce.activity` both use it.
   */
  function visibleRuns(p: Principal): Run[] {
    const own = runsFor(p);
    const ids = new Set(own.map((r) => r.id));
    const sees = seesRun(p);
    const extra = (deps.extraRuns?.(p.company.id) ?? []).filter((r) => !ids.has(r.id) && sees(r));
    return [...own, ...extra];
  }

  const newestFirst = (a: Run, b: Run) => b.id.localeCompare(a.id);

  // Budgets -------------------------------------------------------------------------------------

  function actualMicros(s: AgentsState, b: Budget): number {
    const inPeriod = (r: Run) => !b.period_key || r.created_at.startsWith(b.period_key);
    const runs = s.runs
      .filter(inPeriod)
      .filter((r) =>
        b.subject_kind === "company"
          ? true
          : b.subject_kind === "holder"
            ? r.membership_id === b.subject_id
            : b.subject_kind === "seat"
              ? r.seat_id === b.subject_id
              : false,
      );
    // The month's runs before the ones listed: a steady 40% of the amount for holders.
    const earlier = b.subject_kind === "company" ? 0 : Math.round(b.amount_minor * 0.4) * 10_000;
    return earlier + sum(runs.map((r) => r.cost_micros));
  }

  function reservedMicros(s: AgentsState, b: Budget): number {
    return sum(
      s.runs
        .filter((r) => LIVE.includes(r.state))
        .filter((r) =>
          b.subject_kind === "company"
            ? true
            : b.subject_kind === "holder"
              ? r.membership_id === b.subject_id
              : r.seat_id === b.subject_id,
        )
        .map((r) => r.budget_reserved_micros),
    );
  }

  function detailOf(s: AgentsState, b: Budget) {
    return {
      ...b,
      actual_micros: actualMicros(s, b),
      reserved_micros: reservedMicros(s, b),
      incidents: s.incidents.filter((i) => i.budget_id === b.id && !i.resolved_at),
    };
  }

  const PERIOD = /^\d{4}-(0[1-9]|1[0-2])$/;

  function badBudget(b: Record<string, unknown>, partial: boolean): string | null {
    if (!partial || b.amount_minor !== undefined)
      if (
        typeof b.amount_minor !== "number" ||
        !Number.isInteger(b.amount_minor) ||
        b.amount_minor < 0
      )
        return "amount_minor: a whole amount in minor units, 0 or more";
    if (b.hard_cap_minor !== undefined && b.hard_cap_minor !== null)
      if (typeof b.hard_cap_minor !== "number" || b.hard_cap_minor < 0)
        return "hard_cap_minor: 0 or more";
    if (!partial && typeof b.currency !== "string") return "currency: required";
    if (b.period_key !== undefined && b.period_key !== null && typeof b.period_key === "string")
      if (
        !PERIOD.test(b.period_key) &&
        !/^\d{4}(-Q[1-4])?$/.test(b.period_key) &&
        !/^\d{4}-W\d{2}$/.test(b.period_key) &&
        !/^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/.test(b.period_key)
      )
        return "period_key: not a period key";
    if (b.soft_thresholds !== undefined) {
      const t = b.soft_thresholds;
      if (!Array.isArray(t) || t.length > 5 || t.some((x) => !(Number(x) > 0 && Number(x) <= 1)))
        return "soft_thresholds: up to five fractions between 0 and 1";
    }
    return null;
  }

  const thresholds = (t: unknown): string[] =>
    Array.isArray(t) ? t.map((x) => String(Number(x))) : ["0.8"];

  /**
   * DE-BUDGET-PACE (domain/finance/pace.ts): the DE's spend against its limit this month and the
   * projection at the period's average pace, under the company's (and the DE's own) thresholds.
   */
  function paceOf(company: FakeCompany, de: FakeDe, b: Budget | undefined, actual: number) {
    const bp = company.settings.budget_pace;
    const overrides = bp?.for_de?.[de.membership_id] ?? {};
    const settings = {
      alert_at: overrides.alert_at ?? bp?.alert_at ?? 1.1,
      min_used: overrides.min_used ?? bp?.min_used ?? 0.2,
      second_at: overrides.second_at ?? bp?.second_at ?? 1.3,
      reserve: overrides.reserve ?? bp?.reserve ?? 0.01,
    };
    const spent = Math.round(actual / 10_000);
    // The server's rule (pace-settings.ts `stopCapMinor`): a hard cap stops; so does a DE's own
    // budget at its amount unless it is alert only.
    const stops = b
      ? b.hard_cap_minor !== null || (b.subject_kind === "holder" && !b.alert_only)
      : false;
    const base = {
      budget_id: b?.id ?? null,
      inherited: false,
      currency: b?.currency ?? null,
      capped: stops,
      spent_minor: spent,
      settings,
      overrides,
    };
    if (!b)
      return {
        ...base,
        status: "no_budget" as const,
        limit_minor: null,
        used: 0,
        reserve_minor: null,
        period_start: null,
        period_end: null,
        days_in: 0,
        days_left: null,
        projected_minor: null,
        basis: null,
        runout_at: null,
      };
    const now = deps.now();
    const start = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1);
    const end = Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 1);
    const day = 86_400_000;
    const daysIn = Math.max(1, Math.ceil((now.getTime() - start) / day));
    const total = (end - start) / day;
    const limit = b.hard_cap_minor ?? b.amount_minor;
    const used = limit > 0 ? spent / limit : 0;
    const reserve = Math.round(limit * settings.reserve);
    const projected = Math.round((spent / daysIn) * total);
    const rate = spent / daysIn;
    const runout = rate > 0 ? start + (limit / rate) * day : null;
    const status =
      stops && spent >= limit - reserve
        ? ("reserve_reached" as const)
        : (projected >= settings.alert_at * limit && used >= settings.min_used) ||
            (!stops && spent >= limit && limit > 0)
          ? ("ahead_of_pace" as const)
          : ("on_track" as const);
    return {
      ...base,
      status,
      limit_minor: limit,
      used,
      reserve_minor: stops ? reserve : null,
      period_start: new Date(start).toISOString(),
      period_end: new Date(end).toISOString(),
      days_in: daysIn,
      days_left: Math.max(0, total - daysIn),
      projected_minor: projected,
      basis: "period_average" as const,
      runout_at: runout !== null && runout < end ? new Date(runout).toISOString() : null,
    };
  }

  function deBudget(company: FakeCompany, s: AgentsState, de: FakeDe) {
    const b = s.budgets.find(
      (x) =>
        x.subject_kind === "holder" && x.subject_id === de.membership_id && x.state === "active",
    );
    const reserved = sum(
      s.runs
        .filter((r) => r.membership_id === de.membership_id && LIVE.includes(r.state))
        .map((r) => r.budget_reserved_micros),
    );
    if (!b)
      return {
        budget: null,
        period_key: null,
        actual_micros: 0,
        reserved_micros: reserved,
        paused_for_budget: false,
        pace: paceOf(company, de, undefined, 0),
        amount_unit: "minor" as const,
        minor_per_unit: 100,
        mode: null,
        cap_minor: null,
        resets_at: null,
      };
    const actual = actualMicros(s, b);
    const cap = (b.hard_cap_minor ?? b.amount_minor) * 10_000;
    const pace = paceOf(company, de, b, actual);
    // BUDGET-CAP: a hard cap, or a DE's own budget that is not alert-only, stops new runs.
    const capped = b.hard_cap_minor !== null || !b.alert_only;
    return {
      budget: b,
      period_key: b.period_key,
      actual_micros: actual,
      reserved_micros: reserved,
      paused_for_budget: actual >= cap,
      pace,
      amount_unit: "minor" as const,
      minor_per_unit: 100,
      mode: capped ? ("cap" as const) : ("alert" as const),
      cap_minor: b.hard_cap_minor ?? b.amount_minor,
      resets_at: pace.period_end,
    };
  }

  // Approvals -----------------------------------------------------------------------------------

  function decide(
    p: Principal,
    a: Approval,
    state: "approved" | "rejected" | "edited",
    patch: { reason?: string | null; payload?: Approval["proposal"]; option?: string },
  ): Response {
    const s = stateOf(p.company);
    if (!decides(p, a))
      return forbidden("only the holder of the approver seat decides this approval");
    // Idempotent: a second decision returns the first.
    if (a.state !== "pending") return json(a);
    if (a.expires_at && a.expires_at < stamp()) {
      a.state = "expired";
      a.updated_at = stamp();
      return problem(409, "approval_expired", "Approval expired", "This approval has expired.");
    }
    const mine = p.me.memberships.find((m) => m.company_id === p.company.id)?.membership_id ?? null;
    a.state = state;
    a.decided_at = stamp();
    a.decided_by_membership_id = mine;
    a.reason = patch.reason ?? null;
    a.approved_payload = state === "rejected" ? null : (patch.payload ?? a.proposal);
    // Phase 1 inc 2: what the server applies is applied; the rest says "not applied" and why.
    // CORE-CARDS: the card stays; the chosen option is the decision's.
    const card = a.card;
    Object.assign(a, executionAfterDecision(a.kind, state, stamp()));
    a.card = card;
    a.chosen_option = patch.option ?? null;
    a.updated_at = stamp();
    // The waiting run resumes (approved) or ends (rejected).
    const run = a.run_id ? s.runs.find((r) => r.id === a.run_id) : undefined;
    if (run && run.state === "awaiting_approval") {
      run.state = state === "rejected" ? "completed" : "running";
      run.state_reason = state === "rejected" ? `approval rejected: ${patch.reason ?? ""}` : null;
      if (state === "rejected") {
        run.ended_at = stamp();
        run.budget_reserved_micros = 0;
        run.hand_back = {
          summary: "The proposal was rejected; nothing was sent.",
          needs: "nothing",
        };
        run.summary = run.hand_back.summary ?? null;
      }
      run.updated_at = stamp();
    }
    if (state !== "rejected") deps.onDecided?.(p.company, a);
    if (a.kind === "budget_overrun" && state !== "rejected") {
      const b = s.budgets.find((x) => x.id === a.subject_id);
      const extra = Number(
        (a.approved_payload?.change as { extra_minor?: unknown } | undefined)?.extra_minor ?? 0,
      );
      if (b && Number.isFinite(extra) && extra > 0) {
        b.amount_minor += extra;
        if (b.hard_cap_minor !== null) b.hard_cap_minor += extra;
        b.updated_at = stamp();
        for (const i of s.incidents)
          if (i.budget_id === b.id && !i.resolved_at) {
            i.resolved_at = stamp();
            i.resolved_by_approval_id = a.id;
          }
      }
    }
    return json(a);
  }

  /**
   * Parity P6a: the reads say what the caller may do (the API's domain/approvals/access.ts
   * `approvalAbilities`): view = viewer; decide = decider, still pending and not expired.
   */
  function withAbilities(p: Principal, a: Approval) {
    const expired = a.expires_at !== null && Date.parse(a.expires_at) <= Date.now();
    return {
      ...a,
      can_view: views(p, a),
      can_decide: a.state === "pending" && !expired && decides(p, a),
    };
  }

  function approvalOf(p: Principal, id: string | undefined): Approval | null {
    const a = stateOf(p.company).approvals.find((x) => x.id === id);
    return a && views(p, a) ? a : null;
  }

  // Delegation ----------------------------------------------------------------------------------

  /**
   * Answer a delegation (05: the recipient, or the recipient DE's supervisor; the owner stands
   * in, as for approvals): `sent` → accepted | declined; `accepted` → completed. A repeat of the
   * same answer returns the request; any other move is 409 `conflict`.
   */
  function answerDelegation(
    p: Principal | null,
    id: string | undefined,
    to: "accepted" | "declined" | "completed",
    patch: { result?: Delegation["result"] },
  ): Response {
    const gate = agentsGate(p);
    if (gate || !p) return gate ?? notFound();
    const s = stateOf(p.company);
    const d = s.delegations.find((x) => x.id === id);
    if (!d || !p.manager) return notFound();
    const owner = deps.ownerMembership(p.company.id);
    const mine = p.me.memberships.find((m) => m.company_id === p.company.id)?.membership_id;
    if (!mine || mine !== owner) return forbidden("the recipient or its supervisor answers");
    if (d.state === to) return json(d);
    const from = to === "completed" ? "accepted" : "sent";
    if (d.state !== from) return problem(409, "conflict", "Conflict", `The request is ${d.state}.`);
    d.state = to;
    d.updated_at = stamp();
    if (to !== "completed") d.decided_at = stamp();
    if (patch.result) d.result = patch.result;
    // The recipient (accept) or the requester (decline, complete) is woken.
    const woken = to === "accepted" ? d.to_membership_id : d.from_membership_id;
    const wid = uid(0x9510, companyNo(p.company.id) * 4096 + ++seq);
    event(
      s,
      p.company,
      "wakeup.requested",
      { kind: "wakeup_request", id: wid },
      {
        agent_membership_id: woken,
        kind: "delegation",
        source_ref: { kind: "delegation_request", id: d.id },
      },
      deps.now().getTime(),
    );
    return json(d);
  }

  deps.shareBudgets?.((companyId) => {
    const company = deps.companies.find((c) => c.id === companyId);
    return company ? stateOf(company).budgets : [];
  });
  deps.shareSeatBudget?.((companyId, seatId) => {
    const company = deps.companies.find((c) => c.id === companyId);
    return company
      ? stateOf(company).budgets.some(
          (b) =>
            b.state === "active" &&
            b.subject_kind === "seat" &&
            b.subject_id === seatId &&
            ["day", "week", "month"].includes(b.period_type) &&
            b.amount_minor > 0,
        )
      : false;
  });
  deps.shareApprovals?.((companyId) => {
    const company = deps.companies.find((c) => c.id === companyId);
    return company ? stateOf(company).approvals : [];
  });
  deps.shareRuns?.((companyId) => {
    const company = deps.companies.find((c) => c.id === companyId);
    return [...(company ? stateOf(company).runs : []), ...(deps.extraRuns?.(companyId) ?? [])];
  });

  // Handlers ------------------------------------------------------------------------------------

  return {
    "runs.list": ({ principal: p, url }) => {
      const gate = agentsGate(p);
      if (gate || !p) return gate ?? notFound();
      const state = url.searchParams.get("state");
      const seat = url.searchParams.get("seat_id");
      const member = url.searchParams.get("membership_id");
      const keep = (r: Run) =>
        (!state || r.state === state) &&
        (!seat || r.seat_id === seat) &&
        (!member || r.membership_id === member);
      const own = runsFor(p);
      const ids = new Set(own.map((r) => r.id));
      // This twin's runs first, newest first; then the other twin's, newest first.
      const extra = visibleRuns(p).filter((r) => !ids.has(r.id));
      const rows = [...own.filter(keep).sort(newestFirst), ...extra.filter(keep).sort(newestFirst)];
      return json(page(rows, url));
    },
    "runs.get": ({ principal: p, params }) => {
      const gate = agentsGate(p);
      if (gate || !p) return gate ?? notFound();
      const run = runsFor(p).find((r) => r.id === params.id);
      if (!run) return notFound();
      // DC-07's run detail carries the decision log's first rows; this twin's runs keep none.
      return json({
        ...run,
        steps: stateOf(p.company).steps.get(run.id) ?? [],
        decisions: [],
        decisions_truncated: false,
      });
    },
    "runs.cancel": ({ principal: p, params }) => {
      const gate = agentsGate(p);
      if (gate || !p) return gate ?? notFound();
      const run = runsFor(p).find((r) => r.id === params.id);
      if (!run) return notFound();
      if (run.state === "cancelled") return json(run);
      if (!LIVE.includes(run.state))
        return problem(409, "run_state_conflict", "Run already ended", `The run is ${run.state}.`);
      const mine = p.me.memberships.find((m) => m.company_id === p.company.id);
      run.state = "cancelled";
      run.state_reason = `cancelled by ${mine ? "a person" : "an operator"}`;
      run.ended_at = stamp();
      run.budget_reserved_micros = 0;
      run.lease_owner = null;
      run.lease_until = null;
      run.updated_at = stamp();
      return json(run);
    },

    "approvals.list": ({ principal: p, url }) => {
      const gate = agentsGate(p);
      if (gate || !p) return gate ?? notFound();
      const state = url.searchParams.get("state");
      const kind = url.searchParams.get("kind");
      const mine = url.searchParams.get("approver") === "me";
      const rows = stateOf(p.company)
        .approvals.filter((a) => views(p, a))
        .filter((a) => (!state || a.state === state) && (!kind || a.kind === kind))
        .filter((a) => !mine || decides(p, a))
        .map((a) => withAbilities(p, a));
      return json(page(rows, url));
    },
    "my.approvals": ({ principal: p, url }) => {
      const gate = agentsGate(p);
      if (gate || !p) return gate ?? notFound();
      const rows = stateOf(p.company).approvals.filter(
        (a) => a.state === "pending" && decides(p, a),
      );
      return json(page(rows, url));
    },
    // AGT-UI-2: the same rows as `my.approvals`, counted (the Agents sidebar's badge).
    "my.approvalsCount": ({ principal: p }) => {
      const gate = agentsGate(p);
      if (gate || !p) return gate ?? notFound();
      return json({
        count: stateOf(p.company).approvals.filter((a) => a.state === "pending" && decides(p, a))
          .length,
      });
    },
    "approvals.get": ({ principal: p, params }) => {
      const gate = agentsGate(p);
      if (gate || !p) return gate ?? notFound();
      const a = approvalOf(p, params.id);
      return a ? json(withAbilities(p, a)) : notFound();
    },
    "approvals.approve": ({ principal: p, params }) => {
      const gate = agentsGate(p);
      if (gate || !p) return gate ?? notFound();
      const a = approvalOf(p, params.id);
      return a ? decide(p, a, "approved", {}) : notFound();
    },
    "approvals.reject": ({ principal: p, params, body }) => {
      const gate = agentsGate(p);
      if (gate || !p) return gate ?? notFound();
      const a = approvalOf(p, params.id);
      if (!a) return notFound();
      const reason = (body as { reason?: unknown } | undefined)?.reason;
      if (typeof reason !== "string" || !reason.trim())
        return validation("reason: say why the proposal is rejected");
      return decide(p, a, "rejected", { reason: reason.trim() });
    },
    // CORE-CARDS: a card decided by one of its options (approve or reject, as the option says).
    "approvals.choose": ({ principal: p, params, body }) => {
      const gate = agentsGate(p);
      if (gate || !p) return gate ?? notFound();
      const a = approvalOf(p, params.id);
      if (!a) return notFound();
      const key = (body as { option?: unknown } | undefined)?.option;
      const o = a.card?.options.find((x) => x.key === key);
      if (!o) return validation("option: one of the card's options");
      if (o.decision === "reject")
        return decide(p, a, "rejected", { reason: `Chose: ${o.label}`, option: o.key });
      const payload = o.change !== undefined ? { ...a.proposal, change: o.change } : a.proposal;
      return decide(p, a, "approved", { payload, option: o.key });
    },
    // B2-GATES: "send back with a note" — a rejection that says so; the DE is woken with the note.
    "approvals.sendBack": ({ principal: p, params, body }) => {
      const gate = agentsGate(p);
      if (gate || !p) return gate ?? notFound();
      const a = approvalOf(p, params.id);
      if (!a) return notFound();
      const note = (body as { note?: unknown } | undefined)?.note;
      if (typeof note !== "string" || !note.trim()) return validation("note: say what to change");
      const pending = a.state === "pending";
      const res = decide(p, a, "rejected", { reason: note.trim() });
      if (pending && a.state === "rejected") a.sent_back = true;
      return res.ok ? json(a) : res;
    },
    "approvals.edit": ({ principal: p, params, body }) => {
      const gate = agentsGate(p);
      if (gate || !p) return gate ?? notFound();
      const a = approvalOf(p, params.id);
      if (!a) return notFound();
      const b = (body ?? {}) as { proposal?: Approval["proposal"]; reason?: string };
      if (!b.proposal || typeof b.proposal.title !== "string" || !b.proposal.title.trim())
        return validation("proposal.title: required");
      if (typeof b.proposal.change !== "object" || b.proposal.change === null)
        return validation("proposal.change: an object");
      // AGENT-REPORTING: a reviewer may change the reported number only.
      if (a.kind === "kpi_value") {
        const bad = kpiEditProblem(a.proposal.change, b.proposal.change);
        if (bad) return validation(bad);
      }
      // RECALL-RULES: an approver may change a standing rule's title and text only.
      if (a.kind === "memory_rule") {
        const was = a.proposal.change;
        const now = b.proposal.change as Record<string, unknown>;
        const keys = new Set([...Object.keys(was), ...Object.keys(now)]);
        for (const k of keys)
          if (k !== "title" && k !== "text" && JSON.stringify(was[k]) !== JSON.stringify(now[k]))
            return validation(`proposal.change.${k}: only the title and text may change`);
        for (const k of ["title", "text"])
          if (typeof now[k] !== "string" || !(now[k] as string).trim())
            return validation(`proposal.change.${k}: required`);
      }
      return decide(p, a, "edited", { payload: b.proposal, reason: b.reason ?? null });
    },
    "approvals.create": ({ principal: p, body }) => {
      const gate = agentsGate(p);
      if (gate || !p) return gate ?? notFound();
      const b = (body ?? {}) as Record<string, unknown>;
      const proposal = b.proposal as Approval["proposal"] | undefined;
      if (!proposal?.title) return validation("proposal.title: required");
      const de = deps.desOf(p.company.id)[0];
      const approver = (b.approver_seat_id as string | undefined) ?? de?.supervisor.seat_id;
      if (!approver) return validation("approver_seat_id: no supervisor to default to");
      const t = stamp();
      const mine = p.me.memberships.find((m) => m.company_id === p.company.id)?.membership_id;
      const a: Approval = {
        company_id: p.company.id,
        id: uid(0x9810, companyNo(p.company.id) * 4096 + ++seq),
        kind: b.kind as Approval["kind"],
        subject_kind: String(b.subject_kind),
        subject_id: String(b.subject_id),
        requested_by_seat_id: (b.requested_by_seat_id as string | undefined) ?? null,
        requested_by_membership_id: mine ?? null,
        approver_seat_id: approver,
        run_id: null,
        proposal,
        state: "pending",
        approved_payload: null,
        decided_at: null,
        decided_by_membership_id: null,
        reason: null,
        thread_id: null,
        expires_at: (b.expires_at as string | null | undefined) ?? null,
        executed_at: null,
        ...NO_EXECUTION,
        actor_kind: p.operator ? "agency_user" : "person",
        actor_membership_id: p.operator ? null : (mine ?? null),
        created_at: t,
        updated_at: t,
      };
      // CORE-CARDS: the fake files a card as the server does with the flag on
      const card = b.card as
        | Pick<NonNullable<Approval["card"]>, "situation" | "options" | "recommended">
        | undefined;
      if (card)
        a.card = {
          situation: card.situation,
          options: card.options,
          recommended: card.recommended,
          approver_seat_id: approver,
          deadline: a.expires_at,
          precedents: [],
          slack: null,
        };
      stateOf(p.company).approvals.unshift(a);
      return json(a, 201);
    },

    "budgets.list": ({ principal: p, url }) => {
      if (!p) return notFound();
      if (!p.manager) return forbidden();
      const kind = url.searchParams.get("subject_kind");
      const subject = url.searchParams.get("subject_id");
      const state = url.searchParams.get("state");
      const rows = stateOf(p.company).budgets.filter(
        (b) =>
          (!kind || b.subject_kind === kind) &&
          (!subject || b.subject_id === subject) &&
          (!state || b.state === state),
      );
      return json(page(rows, url));
    },
    "budgets.get": ({ principal: p, params }) => {
      if (!p) return notFound();
      if (!p.manager) return forbidden();
      const s = stateOf(p.company);
      const b = s.budgets.find((x) => x.id === params.id);
      return b ? json(detailOf(s, b)) : notFound();
    },
    "budgets.create": ({ principal: p, body }) => {
      if (!p) return notFound();
      if (!p.manager) return forbidden();
      const b = (body ?? {}) as Record<string, unknown>;
      const bad = badBudget(b, false);
      if (bad) return validation(bad);
      const s = stateOf(p.company);
      const periodKey = (b.period_key as string | null | undefined) ?? null;
      if (b.period_type !== "standing" && !periodKey)
        return validation("period_key: required for every period type but standing");
      const clash = s.budgets.some(
        (x) =>
          x.state === "active" &&
          x.subject_kind === b.subject_kind &&
          x.subject_id === b.subject_id &&
          x.period_type === b.period_type &&
          x.period_key === periodKey,
      );
      if (clash)
        return problem(
          409,
          "conflict",
          "Conflict",
          "This level already has a budget for the period.",
        );
      const t = stamp();
      const mine = p.me.memberships.find((m) => m.company_id === p.company.id)?.membership_id;
      const row: Budget = {
        company_id: p.company.id,
        id: uid(0x9610, companyNo(p.company.id) * 4096 + ++seq),
        subject_kind: b.subject_kind as Budget["subject_kind"],
        subject_id: String(b.subject_id),
        period_type: b.period_type as Budget["period_type"],
        period_key: periodKey,
        amount_minor: b.amount_minor as number,
        currency: b.currency as Budget["currency"],
        soft_thresholds: thresholds(b.soft_thresholds),
        hard_cap_minor: (b.hard_cap_minor as number | null | undefined) ?? null,
        alert_only: (b.alert_only as boolean | undefined) ?? false,
        repeat_monthly: (b.repeat_monthly as boolean | undefined) ?? true,
        inherit: (b.inherit as boolean | undefined) ?? false,
        state: "active",
        actor_kind: p.operator ? "agency_user" : "person",
        actor_membership_id: p.operator ? null : (mine ?? null),
        created_at: t,
        updated_at: t,
      };
      s.budgets.push(row);
      return json(row, 201);
    },
    "budgets.update": ({ principal: p, params, body }) => {
      if (!p) return notFound();
      if (!p.manager) return forbidden();
      const b = stateOf(p.company).budgets.find((x) => x.id === params.id);
      if (!b) return notFound();
      const patch = (body ?? {}) as Record<string, unknown>;
      const bad = badBudget(patch, true);
      if (bad) return validation(bad);
      if (typeof patch.amount_minor === "number") b.amount_minor = patch.amount_minor;
      if (patch.hard_cap_minor !== undefined)
        b.hard_cap_minor = patch.hard_cap_minor as number | null;
      if (patch.soft_thresholds !== undefined)
        b.soft_thresholds = thresholds(patch.soft_thresholds);
      if (typeof patch.inherit === "boolean") b.inherit = patch.inherit;
      if (typeof patch.alert_only === "boolean") b.alert_only = patch.alert_only;
      if (typeof patch.repeat_monthly === "boolean") b.repeat_monthly = patch.repeat_monthly;
      if (typeof patch.currency === "string") b.currency = patch.currency as Budget["currency"];
      b.updated_at = stamp();
      return json(b);
    },
    "budgets.archive": ({ principal: p, params }) => {
      if (!p) return notFound();
      if (!p.manager) return forbidden();
      const b = stateOf(p.company).budgets.find((x) => x.id === params.id);
      if (!b) return notFound();
      if (b.state === "archived")
        return problem(409, "conflict", "Conflict", "The budget is already archived.");
      b.state = "archived";
      b.updated_at = stamp();
      return json(b);
    },
    // ACTIONS: active again, unless its level already has an active budget for the period.
    "budgets.unarchive": ({ principal: p, params }) => {
      if (!p) return notFound();
      if (!p.manager) return forbidden();
      const all = stateOf(p.company).budgets;
      const b = all.find((x) => x.id === params.id);
      if (!b) return notFound();
      if (b.state !== "archived")
        return problem(409, "conflict", "Conflict", "The budget is not archived.");
      const clash = all.some(
        (x) =>
          x.id !== b.id &&
          x.state === "active" &&
          x.subject_kind === b.subject_kind &&
          x.subject_id === b.subject_id &&
          x.period_type === b.period_type &&
          x.period_key === b.period_key,
      );
      if (clash)
        return problem(
          409,
          "conflict",
          "Conflict",
          "This level has an active budget for the period; archive it first.",
        );
      b.state = "active";
      b.updated_at = stamp();
      return json(b);
    },
    "digitalEmployees.budget": ({ principal: p, params }) => {
      if (!p) return notFound();
      if (!p.manager) return forbidden();
      const de = deps.desOf(p.company.id).find((d) => d.membership_id === params.id);
      if (!de) return notFound();
      return json(deBudget(p.company, stateOf(p.company), de));
    },
    // DE-BUDGET-PACE (routes/finance/de-budget.ts): one scope of the pace thresholds, merged.
    "companies.updateBudgetPace": ({ principal: p, body }) => {
      if (!p) return notFound();
      if (!p.manager) return forbidden();
      const b = (body ?? {}) as {
        de_membership_id?: string;
        settings?: Record<string, number | undefined>;
      };
      const mine = Object.fromEntries(
        Object.entries(b.settings ?? {}).filter(([, v]) => typeof v === "number"),
      ) as Record<string, number>;
      const bp = p.company.settings.budget_pace ?? {};
      let next: NonNullable<FakeCompany["settings"]["budget_pace"]>;
      if (b.de_membership_id) {
        if (!deps.desOf(p.company.id).some((d) => d.membership_id === b.de_membership_id))
          return notFound();
        const forDe = { ...bp.for_de };
        if (Object.keys(mine).length) forDe[b.de_membership_id] = mine;
        else delete forDe[b.de_membership_id];
        const { for_de: _old, ...top } = bp;
        next = Object.keys(forDe).length ? { ...top, for_de: forDe } : top;
      } else next = { ...mine, ...(bp.for_de ? { for_de: bp.for_de } : {}) };
      p.company.settings = { ...p.company.settings, budget_pace: next };
      return json(next);
    },
    "digitalEmployees.updateBudget": ({ principal: p, params, body }) => {
      if (!p) return notFound();
      if (!p.manager) return forbidden();
      const de = deps.desOf(p.company.id).find((d) => d.membership_id === params.id);
      if (!de) return notFound();
      const b = (body ?? {}) as Record<string, unknown>;
      const bad = badBudget(b, false);
      if (bad) return validation(bad);
      const s = stateOf(p.company);
      const t = stamp();
      const existing = s.budgets.find(
        (x) =>
          x.subject_kind === "holder" && x.subject_id === de.membership_id && x.state === "active",
      );
      if (existing) {
        existing.amount_minor = b.amount_minor as number;
        existing.currency = b.currency as Budget["currency"];
        existing.period_type = (b.period_type as Budget["period_type"]) ?? existing.period_type;
        existing.period_key = (b.period_key as string | null | undefined) ?? existing.period_key;
        if (b.hard_cap_minor !== undefined)
          existing.hard_cap_minor = b.hard_cap_minor as number | null;
        if (b.soft_thresholds !== undefined)
          existing.soft_thresholds = thresholds(b.soft_thresholds);
        if (b.alert_only !== undefined) existing.alert_only = b.alert_only as boolean;
        if (b.repeat_monthly !== undefined) existing.repeat_monthly = b.repeat_monthly as boolean;
        existing.updated_at = t;
      } else {
        const mine = p.me.memberships.find((m) => m.company_id === p.company.id)?.membership_id;
        s.budgets.push({
          company_id: p.company.id,
          id: uid(0x9610, companyNo(p.company.id) * 4096 + ++seq),
          subject_kind: "holder",
          subject_id: de.membership_id,
          period_type: (b.period_type as Budget["period_type"]) ?? "month",
          period_key: (b.period_key as string | null | undefined) ?? monthKey(deps.now()),
          amount_minor: b.amount_minor as number,
          currency: b.currency as Budget["currency"],
          soft_thresholds: thresholds(b.soft_thresholds),
          hard_cap_minor: (b.hard_cap_minor as number | null | undefined) ?? null,
          alert_only: (b.alert_only as boolean | undefined) ?? false,
          repeat_monthly: (b.repeat_monthly as boolean | undefined) ?? true,
          inherit: false,
          state: "active",
          actor_kind: p.operator ? "agency_user" : "person",
          actor_membership_id: p.operator ? null : (mine ?? null),
          created_at: t,
          updated_at: t,
        });
      }
      return json(deBudget(p.company, s, de));
    },
    "delegationRequests.get": ({ principal: p, params }) => {
      const gate = agentsGate(p);
      if (gate || !p) return gate ?? notFound();
      const d = stateOf(p.company).delegations.find((x) => x.id === params.id);
      return d && p.manager ? json(d) : notFound();
    },
    "delegationRequests.accept": ({ principal: p, params }) =>
      answerDelegation(p, params.id, "accepted", {}),
    "delegationRequests.decline": ({ principal: p, params }) =>
      answerDelegation(p, params.id, "declined", {}),
    "delegationRequests.complete": ({ principal: p, params, body }) => {
      const result = (body as { result?: Delegation["result"] } | undefined)?.result;
      if (!result || typeof result !== "object") return validation("result: required");
      return answerDelegation(p, params.id, "completed", { result });
    },
    "digitalEmployees.agents": ({ principal: p, params }) => {
      const gate = agentsGate(p);
      if (gate || !p) return gate ?? notFound();
      if (!p.operator) return forbidden("agency operators only");
      const de = deps.desOf(p.company.id).find((d) => d.membership_id === params.id);
      if (!de) return notFound();
      return json({
        data: stateOf(p.company).profiles.filter((x) => x.owner_membership_id === de.membership_id),
      });
    },
    // WF-FIX: the wake-ups waiting in self-hosted DEs' inboxes (managers only). The twin's DEs
    // are managed unless a test marks one self-hosted (`selfHost`, with its inbox count); a
    // managed DE's wake-ups become runs, so it never shows here.
    "wakeups.inbox": ({ principal: p }) => {
      const gate = agentsGate(p);
      if (gate || !p) return gate ?? notFound();
      if (!p.manager) return forbidden();
      // As the API: every self-hosted DE with queued wake-ups — retired or removed ones too (the
      // route filters on neither; their queued requests stay) — oldest first, then by membership.
      const data = deps
        .desOf(p.company.id)
        .flatMap((d) => {
          const inbox = d.self_hosted?.inbox ?? [];
          if (!inbox.length) return [];
          const oldest = [...inbox].sort((a, b) => Date.parse(a) - Date.parse(b))[0] as string;
          return [
            {
              membership_id: d.membership_id,
              display_name: d.display_name,
              queued: inbox.length,
              oldest_requested_at: new Date(oldest).toISOString(),
            },
          ];
        })
        .sort(
          (a, b) =>
            Date.parse(a.oldest_requested_at) - Date.parse(b.oldest_requested_at) ||
            a.membership_id.localeCompare(b.membership_id),
        );
      return json({ data, total: data.reduce((n, r) => n + r.queued, 0) });
    },
    "events.list": ({ principal: p, url }) => {
      if (!p) return notFound();
      if (!p.manager) return forbidden();
      const after = Number(url.searchParams.get("after_seq") ?? 0) || 0;
      const types = url.searchParams.getAll("types").flatMap((t) => t.split(","));
      const limit = Math.min(200, Number(url.searchParams.get("limit") ?? 100) || 100);
      // ACTIVITY: one run's events (its trail).
      const runId = url.searchParams.get("actor_run_id");
      if (runId !== null && !UUID.test(runId)) return validation("actor_run_id: not a uuid");
      const data = stateOf(p.company)
        .events.filter(
          (e) =>
            (e.seq ?? 0) > after &&
            (!types.length || types.includes(e.type)) &&
            (!runId || e.actor.run_id === runId),
        )
        .slice(0, limit);
      const last = data.at(-1)?.seq;
      return json({ data, next_cursor: last == null ? null : String(last) });
    },

    // ACTIVITY: Workforce › Activity (services/api routes/agents/activity.ts), over the runs
    // `runs.list` serves the caller: live first (waiting on a person first, then oldest start),
    // the rest newest first; Who and State filters; the task, the "last" line, the waiting
    // approval and the child-run count per row.
    "workforce.activity": ({ principal: p, url }) => {
      const gate = agentsGate(p);
      if (gate || !p) return gate ?? notFound();
      const q = url.searchParams;
      const whoKind = q.get("who_kind");
      const whoId = q.get("who_id");
      const state = q.get("state");
      const parent = q.get("parent_run_id");
      if ((whoKind === null) !== (whoId === null))
        return validation("who_id: who_kind and who_id go together");
      if (whoKind !== null && !["de", "ag", "team"].includes(whoKind))
        return validation("who_kind: de, ag or team");
      if (state !== null && !["live", "waiting", "done", "failed"].includes(state))
        return validation("state: live, waiting, done or failed");
      for (const [k, v] of [
        ["who_id", whoId],
        ["parent_run_id", parent],
      ] as const)
        if (v !== null && !UUID.test(v)) return validation(`${k}: not a uuid`);
      const all = visibleRuns(p);
      // Work's rule for the task title (the work twin: any member who is not only a client
      // viewer opens every task).
      const opensTasks = p.roles.some((r) => r !== "client_viewer");
      const scope =
        whoKind === "ag" || whoKind === "team"
          ? (deps.whoOf?.(p, whoKind, whoId as string) ?? {})
          : {};
      const matchesWho = (r: Run) =>
        whoKind === null ||
        (whoKind === "de" && r.membership_id === whoId) ||
        (whoKind === "ag" && (scope.memberships ?? []).includes(r.membership_id)) ||
        (whoKind === "team" && r.seat_id !== null && (scope.seats ?? []).includes(r.seat_id));
      const byId = new Map(all.map((r) => [r.id, r]));
      // A child folds under its parent when the parent shows too (same Who, no State filter).
      const shown = all.filter(matchesWho).filter((r) => {
        if (parent) return r.parent_run_id === parent;
        const up = r.parent_run_id ? byId.get(r.parent_run_id) : undefined;
        return state !== null || !up || !matchesWho(up);
      });
      const waitingNow = ["awaiting_approval", "waiting_input"];
      const nowStates =
        parent || q.get("cursor")
          ? []
          : state === null || state === "live"
            ? (LIVE as readonly string[])
            : state === "waiting"
              ? waitingNow
              : [];
      const startOf = (r: Run) => r.started_at ?? r.created_at;
      const live = shown
        .filter((r) => nowStates.includes(r.state))
        .sort(
          (a, b) =>
            Number(!waitingNow.includes(a.state)) - Number(!waitingNow.includes(b.state)) ||
            startOf(a).localeCompare(startOf(b)) ||
            a.id.localeCompare(b.id),
        );
      const keepEnded = (r: Run) =>
        parent
          ? true
          : !LIVE.includes(r.state) &&
            (state === null ||
              (state === "waiting" && r.state === "failed" && r.retry_state === "parked") ||
              (state === "done" && (r.state === "completed" || r.state === "cancelled")) ||
              (state === "failed" && r.state === "failed"));
      // The API pages by run id, a UUIDv7 (creation order); the demo's ids are not, so by
      // creation time here (the screen groups days by it).
      const rest =
        state === "live" && !parent
          ? []
          : shown
              .filter(keepEnded)
              .sort((a, b) => b.created_at.localeCompare(a.created_at) || newestFirst(a, b));
      const s = stateOf(p.company);
      const tasks = new Map((deps.tasksOf?.(p.company.id) ?? []).map((t) => [t.id, t]));
      const des = new Map(deps.desOf(p.company.id).map((d) => [d.membership_id, d]));
      const row = (r: Run) => {
        const de = des.get(r.membership_id);
        const task = r.task_id && opensTasks ? tasks.get(r.task_id) : undefined;
        const waiting = s.approvals
          .filter((a) => a.run_id === r.id && a.state === "pending" && views(p, a))
          .sort((a, b) => b.created_at.localeCompare(a.created_at))[0];
        return {
          run: r,
          who: {
            membership_id: r.membership_id,
            name: de?.display_name ?? "Digital employee",
            seat_id: r.seat_id,
            seat_title: de?.seat_title ?? null,
            team: null,
          },
          task: task ? { id: task.id, title: task.title, board_id: task.board_id } : null,
          task_hidden: !!r.task_id && !task,
          last: activityLast(r, s, waiting),
          waiting_approval: waiting
            ? { id: waiting.id, kind: waiting.kind, title: waiting.proposal.title }
            : null,
          child_runs: all.filter((c) => c.parent_run_id === r.id).length,
        };
      };
      const dayStart = Date.parse(q.get("day_start") ?? "") || deps.now().getTime() - 86_400_000;
      const facts = all.filter(matchesWho).reduce(
        (f, r) => {
          const today = Date.parse(r.created_at) >= dayStart;
          return {
            runs_today: f.runs_today + (today ? 1 : 0),
            // The Waiting filter's runs: waiting on an approval or input, or parked for a person.
            waiting:
              f.waiting +
              (waitingNow.includes(r.state) || (r.state === "failed" && r.retry_state === "parked")
                ? 1
                : 0),
            cost_micros_today: f.cost_micros_today + (today ? r.cost_micros : 0),
            tokens_today: f.tokens_today + (today ? r.tokens_in + r.tokens_out : 0),
          };
        },
        { runs_today: 0, waiting: 0, cost_micros_today: 0, tokens_today: 0 },
      );
      const pg = page(rest, url);
      return json({
        ...pg,
        data: pg.data.map(row),
        now: live.slice(0, 100).map(row),
        now_truncated: live.length > 100,
        facts,
      });
    },

    // HOME-DASH: the runtime summary (services/api routes/agents/activity.ts `workforce.runtime`):
    // the runs the caller can view created in `[from, to)` (default the last 7 days, at most 93),
    // summed; an ended run that started and reported neither cost nor tokens is `unreported`.
    "workforce.runtime": ({ principal: p, url }) => {
      const gate = agentsGate(p);
      if (gate || !p) return gate ?? notFound();
      // The contract's own query schema (ISO timestamps with an offset), as the API reads it.
      const parsed = RuntimeQuery.safeParse(Object.fromEntries(url.searchParams));
      if (!parsed.success)
        return validation(
          parsed.error.issues.map((i) => `${i.path.join(".")}: ${i.message}`).join("; "),
        );
      const { from: fromQ, to: toQ } = parsed.data;
      if ((fromQ === undefined) !== (toQ === undefined))
        return validation("to: from and to go together");
      const nowMs = deps.now().getTime();
      const to = toQ === undefined ? nowMs : Date.parse(toQ);
      const from = fromQ === undefined ? to - 7 * 86_400_000 : Date.parse(fromQ);
      if (from >= to) return validation("to: to must be after from");
      if (to - from > 93 * 86_400_000) return validation("from: at most 93 days");
      const runs = visibleRuns(p).filter((r) => {
        const at = Date.parse(r.created_at);
        return at >= from && at < to;
      });
      const live = (r: Run) => LIVE.includes(r.state);
      const used = (r: Run) => r.cost_micros > 0 || r.tokens_in > 0 || r.tokens_out > 0;
      return json({
        from: new Date(from).toISOString(),
        to: new Date(to).toISOString(),
        runs: runs.length,
        des: new Set(runs.map((r) => r.membership_id)).size,
        live: runs.filter(live).length,
        active_ms: runs.reduce((n, r) => {
          if (!r.started_at) return n;
          const end = !r.ended_at && live(r) ? nowMs : Date.parse(r.ended_at ?? r.started_at ?? "");
          return n + Math.max(0, end - Date.parse(r.started_at));
        }, 0),
        tokens_in: runs.reduce((n, r) => n + r.tokens_in, 0),
        tokens_out: runs.reduce((n, r) => n + r.tokens_out, 0),
        cost_micros: runs.reduce((n, r) => n + r.cost_micros, 0),
        metered: runs.filter(used).length,
        unreported: runs.filter((r) => r.started_at && !live(r) && !used(r)).length,
      });
    },
  };
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** The row's "last" line, by the API's rules (routes/agents/activity.ts `lastOf`). */
function activityLast(
  r: Run,
  s: AgentsState,
  waiting: Approval | undefined,
): {
  kind: "checkpoint" | "event" | "approval" | "summary" | "reason" | "step";
  text: string;
  at: string;
} | null {
  const ended = r.ended_at ?? r.updated_at;
  if (r.state === "completed")
    return { kind: "summary", text: r.summary?.trim() || "Done", at: ended };
  if (r.state === "failed" || r.state === "cancelled")
    return {
      kind: "reason",
      text: `${r.state_reason ?? (r.state === "failed" ? "failed" : "stopped")}${
        r.retry_state === "scheduled"
          ? " · retry scheduled"
          : r.retry_state === "parked"
            ? " · parked for a person"
            : r.attempt > 1
              ? ` · attempt ${r.attempt}`
              : ""
      }`,
      at: ended,
    };
  if (r.state === "awaiting_approval")
    return {
      kind: "approval",
      text: waiting ? `Waiting for approval: ${waiting.proposal.title}` : "Waiting for an approval",
      at: r.updated_at,
    };
  const steps = s.steps.get(r.id) ?? [];
  const cp = [...steps]
    .reverse()
    .find(
      (x) =>
        x.kind === "note" &&
        typeof x.payload.summary === "string" &&
        x.payload.summary.startsWith("Checkpoint:") &&
        x.payload.audience !== "agent",
    );
  const ev = [...s.events]
    .reverse()
    .find((e) => e.actor.run_id === r.id && ACTIVITY_EVENTS.includes(e.type));
  if (cp && (!ev || cp.created_at >= ev.occurred_at))
    return {
      kind: "checkpoint",
      text: String(cp.payload.summary).replace(/^Checkpoint:\s*/, ""),
      at: cp.created_at,
    };
  if (ev) {
    const p = ev.payload as Record<string, unknown>;
    const text =
      ev.type === "task.status_changed"
        ? p.completed === true
          ? "Completed the task"
          : `Moved the task to ${String(p.to)}`
        : ev.type === "task.handed_off"
          ? "Handed the task to a seat"
          : ev.type === "task.assigned"
            ? "Assigned the task to a seat"
            : ev.type === "task.created"
              ? "Created a task"
              : ev.type === "message.created"
                ? p.thread_kind === "task"
                  ? "Commented on the task"
                  : "Posted a message"
                : "Asked for an approval";
    return { kind: "event", text, at: ev.occurred_at };
  }
  const st = steps.at(-1);
  if (!st) return null;
  const p = st.payload;
  const text =
    p.audience === "agent"
      ? "Working"
      : typeof p.summary === "string"
        ? p.summary
        : typeof p.tool === "string"
          ? p.tool
          : typeof p.name === "string"
            ? p.name
            : st.kind === "model_call"
              ? "Thinking"
              : st.kind === "tool_call"
                ? "Calling a tool"
                : st.kind === "proposal"
                  ? "Proposed a change"
                  : "Note";
  return { kind: "step", text, at: st.created_at };
}

const ACTIVITY_EVENTS: readonly string[] = [
  "task.handed_off",
  "task.assigned",
  "task.status_changed",
  "task.created",
  "message.created",
  "approval.requested",
];
